import React, { useState, useRef } from 'react';
import { FiMic, FiSquare, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';

interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onRecordingComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        onRecordingComplete(audioBlob);
        audioChunksRef.current = [];
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started');
    } catch (error) {
      toast.error('Could not start recording. Please check microphone permissions.');
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording stopped');
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      audioChunksRef.current = [];
      toast('Recording canceled');
    }
  };

  return (
    <div className="flex items-center gap-2">
      {!isRecording ? (
        <button onClick={startRecording} className="p-2 text-gray-500 hover:text-blue-500">
          <FiMic />
        </button>
      ) : (
        <>
          <button onClick={stopRecording} className="p-2 text-green-500 hover:text-green-600">
            <FiSquare />
          </button>
          <button onClick={cancelRecording} className="p-2 text-red-500 hover:text-red-600">
            <FiTrash2 />
          </button>
        </>
      )}
    </div>
  );
};

export default VoiceRecorder;
